import type { UseQueryResult } from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import type { GetSharedData } from "../types/common";
import type { ReportJurisdictionsResponse } from "../types/editReport";
import type { CounterpartyVersionCompareResponse } from "../types/opinionCompare";
import type { CategoryDetailsByOpinionIdResponse,DecryptDataResponse, ExportReportExcelResponse, ExportToExcelViewResponse, ExportToXMLResponse, FactPatternListResponse, FileTypeDetailsApiItem, FileTypeDetailsApiResponse, GenerateReportPDFRequest, GenerateTabReportExcelRequest, GenerateTabReportPDFRequest, GetColorOptionsResponse, GetLatestReportIdResponse, GetOpinionContentTileResponse, GetOrganisationDetailResponse, GetQuestionAndAnswerListResponse, GetRegionDataByJuriIdResponse, GetReportFileByApiResponse, GetReportOverviewConfigResponse, getReportQuestionResponse, GetReportRelatedLinkResponse, GetReportResponseTypeListResponse, GetReportTemplatesResponse, GetRportAnswerResponseTypeListResponse, GetSRCommentResponse, GetSRReletedAlertResponse, GetTreeTypeResponse, GrayOutListResponse, GridExportToExcelAccessResponse, GridExportToPdfAccessResponse, InternalReferenceResponse, OpinionAccessResponse, ProductAccessListResponse, QuestionExistTemplateResponse, ReportNodesDetailsResponse, ReportNoteContactsResponse, ReportNoteGuidanceAttachmentResponse, ReportNoteGuidanceResponse, ReportNoteResourcesResponse, ReportNotesResourcesResponse, ReportVersionResponseData, ReportWindowResponse, SingleReportResponse, SRConfigResponse, ViewDocumentMutationParameters } from "../types/singleReport";
import { keyFactory } from "../util/keyFactory";
import authService from "./authService";
import { FileExportClient,HttpClient } from "./axiosConfig";

export const useSRDisplayConfig = (reportId : string) => {
  return useQuery({
    queryKey: [keyFactory.getSRDisplayConfig, reportId],
    queryFn: async () => {
      const result = await HttpClient.get<SRConfigResponse>(`users/reports/${reportId}/display-configs`);
      return result.data.data ?? null;
    },
    enabled: !!reportId,
  });
};

export const useGetReportRelatedLinks = (eventUrl:string) => {
  const isValidEventUrl =
    typeof eventUrl === 'string' && eventUrl.trim().length > 0;
  return useQuery({
    queryKey: [keyFactory.getReportRelatedLink,eventUrl],
    queryFn: async () => {
      const result = await HttpClient.get<GetReportRelatedLinkResponse>(`users/products/${eventUrl}/related-links`);
      return result.data.data ?? null;
    },
    enabled: isValidEventUrl,
  });
};

// this is temporary, will be removed once the API is ready and take it from product home page
export const useGetReportRelatedAlerts = (parameters: { jurisdictionId: string; sourceDate: string; reportId: string,productId:string }) => {

  const { jurisdictionId, sourceDate, reportId,productId } = parameters || {};
  const isValid = jurisdictionId && sourceDate && reportId;

  return useQuery({
    queryKey: [keyFactory.getReportRelatedAlerts, jurisdictionId, sourceDate, reportId,productId],
    queryFn: async () => {
      const result = await HttpClient.get<GetSRReletedAlertResponse>(`users/products/${productId}/releted-alerts?jurisdictionId=${jurisdictionId}&sourceDate=${sourceDate}&reportId=${reportId}`);
      return result.data.data ?? null;
    },
       enabled: !!isValid, // API only runs when all values are defined
  });
};

export const useSingleGetReport = (reportID: string, enabled = true) => {
  return useQuery({
    queryKey: [keyFactory.getSingleReport, reportID],
    queryFn: async () => {
      const result = await HttpClient.get<SingleReportResponse>(
        `users/reports/report-single`,
        {
          params: { reportIds: reportID },
        }
      );
      return result.data.data ?? null;
    },
    enabled: !!reportID && enabled,
    staleTime: 0,
    gcTime: 0,
  });
};

export const getSingleReport = async (reportID: string) => {
  const result = await HttpClient.get<SingleReportResponse>(
    `users/reports/report-single`,
    {
      params: { reportIds: reportID },
    }
  );
  return result.data.data ?? null;
};
export const useOpinionContentTile = (
  reportID: string, 
  filterKey?: string, 
  filterValue?: any
) => {
  return useQuery({
    queryKey: [keyFactory.getOpinionContentTileId, reportID, filterKey, filterValue], // dynamic key in the cache
    queryFn: async () => {
      const parameters = new URLSearchParams();
      parameters.append('opinionContentTileId', '-1');

      if (filterKey && filterValue !== undefined && filterValue !== null) {
        parameters.append(filterKey, filterValue.toString());
      }

      const url = `users/reports/${reportID}/content-tiles?${parameters.toString()}`;

      const result = await HttpClient.get<GetOpinionContentTileResponse>(url);
      return result.data.data ?? null;
    },
    enabled: !!reportID,
    staleTime: 0,
    gcTime: 0,
  });
};

export const useGetReportAnalysisOverview = (parameters: { productOrgId: string; reportId: string }) => {
  
  const { productOrgId, reportId } = parameters || {};
  const isValid = productOrgId && reportId;

  return useQuery({
    queryKey: [keyFactory.getReportAnalysisOverview, productOrgId, reportId],
    queryFn: async () => {
      const result = await HttpClient.get<GetReportOverviewConfigResponse>(`users/reports/${reportId}/analysis-overview-part2?productOrgId=${productOrgId}`);
      return result.data.data ?? null;
    },
       enabled: !!isValid, // API only runs when all values are defined
  });
};

export const useGetOrgByRenderingStyleId = (productID?: string) => {
  const numericProductId = Number(productID);

  const isValidProductId =
    typeof productID === 'string' &&
    productID.trim() !== '' &&
    Number.isFinite(numericProductId) &&
    numericProductId > 0;

  return useQuery({
    queryKey: [keyFactory.getOpinionContentTileId, productID, numericProductId],
    queryFn: async () => {
      const result = await HttpClient.get<GetOrganisationDetailResponse>(
        `users/products/${numericProductId}/organisations`
      );
      return result.data.data ?? null;
    },
    enabled: isValidProductId,
  });
};


export const useGetColorOptions = (reportID: string) => {
  return useQuery({
    queryKey: [keyFactory.getColorOptions, reportID],
    queryFn: async () => {
      const result = await HttpClient.get<GetColorOptionsResponse>(`users/products/products/color-options?reportIds=${reportID}`);
      return result.data.data ?? null;
    },
    enabled: !!reportID
  });
};

export const useGetLatestReportId = (reportID: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: [keyFactory.getLatestReportId, reportID],
    queryFn: async () => {
      const result = await HttpClient.get<GetLatestReportIdResponse>(`users/reports/latest-report-Id?reportId=${reportID}`);
      return result.data.data ?? null;
    },
    enabled: enabled && !!reportID
  });
};

export const useGetReportTemplates = (parameters: {reportId?: string, reportIds?: string, templateIds: string}) => {
  const { templateIds, reportId, reportIds } = parameters || {};
  const effectiveReportIds = reportIds ?? reportId ?? '';
  const isValid = effectiveReportIds && templateIds;
  return useQuery({
    queryKey: [keyFactory.getReportTemplates, effectiveReportIds, templateIds],
    queryFn: async () => {
      const result = await HttpClient.get<GetReportTemplatesResponse>(`users/reports/templates?templateIds=${templateIds}`,{
        params:{reportIds: effectiveReportIds,reportId:0,type:2,chkTemplateCount:0}
      });
      return result.data.data ?? null;
    },
     enabled: !!isValid,
  });
};  

export const useGetReportQuestionAnswer = (parameters: {reportIds: string, productId: string, templateIds: string, questionIds: string, subjectId: string}) => {
  const { templateIds, reportIds, productId, questionIds, subjectId } = parameters || {};
  const isValid = reportIds && templateIds && productId && questionIds && subjectId;
  return useQuery({
    queryKey: [keyFactory.getReportTemplates, reportIds, templateIds, productId, questionIds, subjectId],
    queryFn: async () => {
      const result = await HttpClient.get<GetQuestionAndAnswerListResponse>(`/users/products/${productId}/reports/templates/questions-answers?reportIds=${reportIds}&templateIds=${templateIds}&questionIds=${questionIds}&productOrgId=${productId}&subjectId=${subjectId}`);
      return result.data.data ?? null;
    },
     enabled: !!isValid,
  });
};  

export const useGetReportResponseType = () => {
  return useQuery({
    queryKey: [keyFactory.getReportResponseType],
    queryFn: async () => {
      const result = await HttpClient.get<GetReportResponseTypeListResponse>(`users/reports/response-types`);
      return result.data.data ?? null;
    },
  });
};

export const useGetReportAnswerResponseType = (parameters: { questionIds: number, typeId: number }) => {
  const isValid = parameters?.questionIds && parameters?.typeId
  return useQuery({
    queryKey: [keyFactory.getAnswerResponseType, parameters?.questionIds, parameters?.typeId],
    queryFn: async () => {
      const result = await HttpClient.get<GetRportAnswerResponseTypeListResponse>(`users/questions/${parameters?.questionIds}/response-types?typeId=${parameters?.typeId}`);
      return result.data.data ?? null;
    },
     enabled: !!isValid,
  });
};

export const useGetTreeType = (parameters: { typeId: number }) => {
  return useQuery({
    queryKey: [keyFactory.getTreeType, parameters?.typeId],
    queryFn: async () => {
      const result = await HttpClient.get<GetTreeTypeResponse>(`users/questions/tree-types?treeTypeId=${parameters?.typeId}`);
      return result.data.data ?? null;
    },
  });
};

export const useGetFactPattern = (reportIds: string) => {
  const isValid = reportIds
  return useQuery({
    queryKey: [keyFactory.getFactPattern, reportIds],
    queryFn: async () => {
      const result = await HttpClient.get<FactPatternListResponse>(`users/products/products/fact-patterns?reportIds=${reportIds}`);
      return result.data.data ?? null;
    },
     enabled: !!isValid,
  });
};

export const useGetGrayOutResponse = (parameters: { reportId: string, productOrgId: string, templateId: string, questionIds: string, blockId: string, subBlockId: string }) => {
  return useQuery({
    queryKey: [keyFactory.getGrayOutResponse, parameters?.reportId, parameters?.productOrgId, parameters?.templateId, parameters?.questionIds, parameters?.blockId, parameters?.subBlockId],
    queryFn: async () => {
      const result = await HttpClient.get<GrayOutListResponse>(`users/answers/response-greyouts?questionIds=${parameters.questionIds}&reportId=${parameters?.reportId}&productOrgId=${parameters?.productOrgId}&templateId=${parameters?.templateId}&blockId=${parameters?.blockId}&subBlockId=${parameters?.subBlockId}`);
      return result.data.data ?? null;
    },
    enabled: !!parameters?.reportId && !!parameters?.productOrgId && !!parameters?.templateId && !!parameters?.questionIds && !!parameters?.blockId && !!parameters?.subBlockId,
  });
};

export type FileTypeDetailsSingleParameters = {
  fileTypeId: number;
  responseId?: number;
  partId?: number;
  reportId?: number;
  docId?: number;
};

/** Load all file-type icons (CF getFileIcons1) — use with getFileIconFileNameByExtension */
export type FileTypeDetailsListParameters = {
  fileTypeId: -1;
  responseId?: number;
  partId?: number;
  reportId?: number;
  docId?: number;
};

export function useFileTypeDetails(
  parameters: FileTypeDetailsListParameters
): UseQueryResult<Array<FileTypeDetailsApiItem>>;
export function useFileTypeDetails(
  parameters: FileTypeDetailsSingleParameters
): UseQueryResult<FileTypeDetailsApiItem | null>;
export function useFileTypeDetails(
  parameters: FileTypeDetailsListParameters | FileTypeDetailsSingleParameters
): UseQueryResult<FileTypeDetailsApiItem | Array<FileTypeDetailsApiItem> | null> {
  const {
    fileTypeId,
    responseId = 0,
    partId = 0,
    reportId = 0,
    docId: documentId = 0,
  } = parameters;

  return useQuery({
    queryKey: [
      keyFactory.getFileTypeDetails,
      fileTypeId,
      responseId,
      partId,
      reportId,
      documentId,
    ],
    queryFn: async () => {
      const result = await HttpClient.get<FileTypeDetailsApiResponse>(
        `users/file-type-details`,
        {
          params: {
            fileTypeId,
            responseId,
            partId,
            reportId,
            docId: documentId,
          },
        }
      );
      const list = result.data.data ?? [];

      if (fileTypeId === -1) {
        return list;
      }

      return list[0] ?? null;
    },
  });
};


export const useDecryptEurl = (eurl : string | null) => {
  return useQuery({
    queryKey: [keyFactory.getDecryptEurl, eurl],
    queryFn: async () => {
      const result = await HttpClient.get<DecryptDataResponse>(`commons/decrypt?params=${eurl}`);
      return result.data.data ?? null;
    },
    enabled: !!eurl,
    retry: false,
  });
};

// Non-hook version for use in async functions
export const decryptEurl = async (eurl: string) => {
  const result = await HttpClient.get<DecryptDataResponse>(`commons/decrypt?params=${eurl}`);
  return result.data.data ?? null;
};

export const useGetProductAccess = (productId : string) => {
  return useQuery({
    queryKey: [keyFactory.getProductAccess, productId],
    queryFn: async () => {
      const result = await HttpClient.get<ProductAccessListResponse>(`users/products/${productId}/product-api-access`);
      return result.data.data ?? null;
    },
    enabled: !!productId,
  });
};

const isValidId = (id: string): boolean => {
  const number = Number(id);
  return number === -1 || number > 0;
}
export const useGetQuestionExistInTemplate = (parameters: { reportId: string, templateId: string, questionId: string }) => {

   const isValid =
    isValidId(parameters?.reportId) &&
    isValidId(parameters?.templateId) &&
    isValidId(parameters?.questionId);

  return useQuery({
    queryKey: [keyFactory.getQuestionExistInTemplate, parameters?.reportId, parameters?.templateId, parameters?.questionId],
    queryFn: async () => {
      const result = await HttpClient.get<QuestionExistTemplateResponse>(`users/reports/${parameters?.reportId}/question-template?templateId=${parameters?.templateId}&questionId=${parameters?.questionId}`);
      return result.data.data ?? null;
    },
    enabled: isValid,
  });
};

export const useGetReportComments = (reportId : string) => {
  return useQuery({
    queryKey: [keyFactory.getSingleReportComment, reportId],
    queryFn: async () => {
      const parameters = reportId?.split(',')?.length > 1 ? { reportId: 0, reportIds: reportId } : { reportId: reportId };
      const result = await HttpClient.get<GetSRCommentResponse>(`users/reports/recent-changes/comments`, { params: parameters });
      return result.data.data ?? null;
    },
    enabled: !!reportId,
  });
};
export const useGetWindowData = (reportId?: string) => {
  return useQuery({
    queryKey: [keyFactory.getEffectiveDate, reportId],
    queryFn: async () => {
      const result = await HttpClient.get<ReportWindowResponse>(
        `users/reports/window-data`,
          {
          params: { reportIds: reportId },
        }
      );
      return result.data.data ?? null;
    },
    enabled: !!reportId,
    staleTime: 0,
    gcTime: 0,
  });
};

export const useGetVersion = (reportId?: string) => {
  return useQuery({
    queryKey: [keyFactory.getVersion,reportId],
    queryFn: async () => {
      const result = await HttpClient.get<ReportVersionResponseData>(`users/reports/current-version?reportId=${reportId}`);
      return result.data.data ?? null;
    },
    enabled: !!reportId,
  });
};
 
export const useGetContactDetails = (reportId?: string) => {
  return useQuery({
    queryKey: [keyFactory.getContactDetails,reportId],
    queryFn: async () => {
      const result = await HttpClient.get<ReportNoteContactsResponse>(`users/reports/${reportId}/notes-contacts?notesContactsId=0&reportIdList=0`);
      return result.data.data ?? null;
    },
    enabled: !!reportId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
};
 
export const useGetGuidanceDetails = (reportId?: string) => {
  return useQuery({
    queryKey: [keyFactory.getGuidanceDetails,reportId],
    queryFn: async () => {
      const result = await HttpClient.get<ReportNoteGuidanceResponse>(`users/reports/${reportId}/notes-guidance?notesGuidanceId=0`);
      return result.data.data ?? null;
    },
    enabled: !!reportId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
};
export const useGetResourcesDetails = (reportId?: string) => {
  return useQuery({
    queryKey: [keyFactory.getResourcesDetails,reportId],
    queryFn: async () => {
      const result = await HttpClient.get<ReportNoteResourcesResponse>(`users/reports/${reportId}/notes-resources-details?addNotesResourcesId=0`);
      return result.data.data ?? null;
    },
    enabled: !!reportId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
};
 export const useGuidanceAttachment = (guidanceId?: number) => {
  return useQuery({
    queryKey: [keyFactory.getGuidanceAttachment,guidanceId],
    queryFn: async () => {
      const result = await HttpClient.get<ReportNoteGuidanceAttachmentResponse>(`users/notes-guidance-attachment-by-ids?addNotesGuidanceId=${guidanceId}&addNotesGuidanceAttachmentId=0`);
      return result.data.data ?? null;
    },
    enabled: !!guidanceId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
};


export const useGetCounterpartyVersionCompare = (reportId?: string) => {
  return useQuery({
    queryKey: [keyFactory.getCounterpartyVersionCompare, reportId],
    queryFn: async () => {
      const result = await HttpClient.get<CounterpartyVersionCompareResponse>(
        `users/counter-party/version-compare?reportId=${reportId}`
      );
      return result.data.data ?? null;
    },
    enabled: !!reportId,
  });
};
  export const useResourceAttachment = (resourceId?: number) => {
  return useQuery({
    queryKey: [keyFactory.getResourcesAttachment,resourceId],
    queryFn: async () => {
      const result = await HttpClient.get<ReportNotesResourcesResponse>(`users/notes-resources-attachments?notesResourcesId=${resourceId}&notesResourcesAttachmentId=0
 `);
      return result.data.data ?? null;
    },
    enabled: !!resourceId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
};
export const usePinnedDocuments = (reportId?: string) => {
  const numericReportId = Number(reportId);

  const isValidReportId =
    typeof reportId === 'string' &&
    reportId.trim() !== '' &&
    !Number.isNaN(numericReportId) &&
    numericReportId > 0;

  return useQuery({
    queryKey: [keyFactory.getPinnedDocument, reportId, numericReportId],
    queryFn: async () => {
      const result = await HttpClient.get<ReportNotesResourcesResponse>(
        `users/${numericReportId}/pinned-documents`
      );
      return result.data.data ?? null;
    },
    enabled: isValidReportId,
  });
};
export const useGetOpinionAccess = (reportId?: string) => {
  return useQuery({
    queryKey: [keyFactory.getOpinionAccess,reportId],
    queryFn: async () => {
      const result = await HttpClient.get<OpinionAccessResponse>(`users/reports/${reportId}/access`);
      return result.data.data ?? null;
    },
    enabled: !!reportId,
  });
};
export const useGetExportToExcelView = (productID: string) => {
  return useQuery({
    queryKey: [keyFactory.getExportToExcelView, productID],
    queryFn: async () => {
      const result = await HttpClient.get<ExportToExcelViewResponse>(`users/products/products/${productID}/export-to-excel-report-view`);
      return result.data.data ?? null;
    },
    enabled: !!productID
  });
};
export const useGetGridExportToExcelAccess = (productID: string) => {
  return useQuery({
    queryKey: [keyFactory.getGridExportToExcelAccess, productID],
    queryFn: async () => {
      const result = await HttpClient.get<GridExportToExcelAccessResponse>(`users/products/${productID}/grid-export-to-excel-access`);
      return result.data.data ?? null;
    },
    enabled: !!productID
  });
};
export const useGetGridExportToPdfAccess = (productID: string) => {
  return useQuery({
    queryKey: [keyFactory.getGridExportToPdfAccess, productID],
    queryFn: async () => {
      const result = await HttpClient.get<GridExportToPdfAccessResponse>(`users/products/${productID}/grid-export-to-pdf-access`);
      return result.data.data ?? null;
    },
    enabled: !!productID
  });
};
export const useGetExportToXML = (productID: string) => {
  return useQuery({
    queryKey: [keyFactory.getExportToXMl, productID],
    queryFn: async () => {
      const result = await HttpClient.get<ExportToXMLResponse>(`users/products/products/${productID}/check-access-export-to-xml`);
      return result.data.data ?? null;
    },
    enabled: !!productID
  });
};

export const useClearCache = (
    EUrl: string, 
    productID: string, 
    orgId: number, 
    dynamicKey: string,
    options?: { enabled?: boolean }
  ) => {
    return useQuery({
      queryKey: [keyFactory.getClearCache, EUrl, productID, orgId, dynamicKey],
      queryFn: async () => {
        const result = await HttpClient.get<ExportToXMLResponse>(
          `users/reports/cache-clean?eUrl=${EUrl}&operation=clearcache&reportId=${productID}&${dynamicKey}=true&productOrgId=${orgId}&clearCache=1&templateIds&questionIds`
        );
        return result.data.data ?? null;
      },
      enabled: options?.enabled ?? false,
    });
};



export const useGetReportQuestions = (parameters: {
  reportIds: string;
  templateIds: string;
  questionIds: string;
  productOrgId: number;
}) => {
  const { templateIds, reportIds, questionIds, productOrgId } = parameters || {};

  const isValid = !!(reportIds && templateIds && questionIds && productOrgId);

  return useQuery({
    queryKey: [keyFactory.getReportQuestion, reportIds, templateIds, questionIds, productOrgId],
    queryFn: async () => {
      const result = await HttpClient.get<getReportQuestionResponse>(
        `users/reports/questions`,
        {
          params: {
            reportIds,            
            questionIds,
            templateIds,
            productOrgId,
          },
        }
      );
      return result.data.data ?? null;
    },
    enabled: isValid,
  });
};
export const useStorageMutation = (
  opinionId?: number,
  // opinionCompare: number,
) => {
  return useMutation({
    mutationFn: async ({ filename, path, type , location , contentTileName, compare , part ,viewResource , prodId,pageId, gridID,renStyleID,prodID, alertId, isListofLink,renStyleId, isfeatureItem}: { filename: string; path?: string; type?: string , compare? : number , opinionId? : string , location? : string , contentTileName? : string , part? : string , viewResource? : number , prodId?: string , pageId?: number, gridID?: number ,renStyleID?: number, renStyleId ?:number,prodID?:number, alertId?: string, isListofLink?: number, isfeatureItem?: number }) => {
      const response = await HttpClient.get('/storage/view-doc?', {
        responseType: 'blob',
        params: {
          filename,
          path: path,
          opinionID: opinionId,
          type,
          compare,
          location,
          contentTileName,
          part,
          viewResource,
          prodId,
          pageId,
          gridID,
          renStyleID: renStyleID,
          prodID,
          alertId,
          isListofLink,
          renStyleId,
          isfeatureItem
        },
      });
      
      return response; // return full AxiosResponse<Blob> so headers available
    },
  });
};

export const useAuthViewDocumentMutation = () => {
  return useMutation({
    mutationKey: [keyFactory.getAuthViewDoc],
    mutationFn: async ({ filename, path, renStyleID, type, compare, prodID, location, isMtob }: ViewDocumentMutationParameters) => {
      return authService.getAuthViewDocument({
        filename,
        path,
        renStyleID,
        type,
        compare,
        location,
        prodID,
        isMtob,
      }) as Promise<AxiosResponse<Blob>>;
    },
  });
};

export const useGetAuthViewDocumentDownload = (
  filename: string,
  path: string | undefined,
  renStyleID: number | undefined,
  location: string,
  isMtob: number | undefined,
  enabled: boolean,
) => {
  return useQuery<GetSharedData>({
    queryKey: [keyFactory.getAuthViewDoc, 'json', filename, path, renStyleID, location, isMtob],
    queryFn: async () => {
      return authService.getAuthViewDocument({
        filename,
        path,
        renStyleID,
        prodID: renStyleID,
        type: 'conditionsOfUse',
        compare: 0,
        location,
        isMtob,
        json: true,
      }) as Promise<GetSharedData>;
    },
    enabled: enabled && !!filename && renStyleID != null,
  });
};
export const useStorageMutationForPages = (
  opinionId?: number,
) => {
  return useMutation({
    mutationFn: async ({ filename, path, type , location , contentTileName, compare , part ,viewResource , prodId,pageId}: { filename: string; path?: string; type?: string , compare? : number , opinionId? : string , location? : string , contentTileName? : string , part? : string , viewResource? : number , prodId?: string , pageId?: number }) => {
      const response = await HttpClient.get('/storage/view-doc?', {
        responseType: 'blob',
        params: {
          filename,
          path: path,
          type,
          compare,
          location,
          contentTileName,
          part,
          viewResource,
          prodId,
          pageId
        },
      });
      
      return response; // return full AxiosResponse<Blob> so headers available
    },
  });
};

export const useGetCategoryDetailsByOpinionId = (opinionId: string) => {
  return useQuery({
    queryKey: [keyFactory.getCategoryDetailsByOpinionId, opinionId],
    queryFn: async () => {
      const result = await HttpClient.get<CategoryDetailsByOpinionIdResponse>(`/users/reports/${opinionId}/categories`);
      return result.data.data ?? null;
    },
    enabled: !!opinionId
  });
};

export interface GetReportGuidanceAttachmentsRequest {
  eurl: string;
  reportId: number;
}

export const getReportGuidanceNotesAttachments = async ({
  eurl,
  reportId,
}: GetReportGuidanceAttachmentsRequest): Promise<AxiosResponse<Blob>> => {
  const response = await HttpClient.get<Blob>(
    '/storage/reports/notes-guidance/attachments',
    {
      responseType: 'blob', // Important for file download
      params: {
        eurl,
        reportId,
      },
    }
  );

  return response;
};

export const useReportGuidanceNotesAttachmentsMutation = () => {
  return useMutation({
    mutationFn: async ({ eurl, reportId }: { eurl: string; reportId: number }) => {
      return getReportGuidanceNotesAttachments({ eurl, reportId });
    },
  });
};


export const useGetJurisdictionNameForOpinionCompare = (reportId: string) => {
  return useQuery({
    queryKey: [keyFactory.getJurisdictionNameForOpinionCompare, reportId],
    queryFn: async () => {
      const result = await HttpClient.get<ReportJurisdictionsResponse>(
        `users/reports/compare/jurisdictions?reportIds=${reportId}`
      );
      return result.data.data ?? null;
    },
    enabled: !!reportId,
  });
};

export const generateReportPDF = async ({
  eUrl,
}: GenerateReportPDFRequest): Promise<AxiosResponse<Blob>> => {
  const parameters = new URLSearchParams();

  parameters.append('eUrl', eUrl);

  const response = await FileExportClient.get<Blob>(
    `/users/reports/single-or-multi-template/pdf/generate`,
    {
      responseType: 'blob',
      params: parameters,
    }
  );

  return response;
};

export const useGenerateReportPDFMutation = () => {
  return useMutation({
    mutationFn: async (parameters: GenerateReportPDFRequest) => {
      return generateReportPDF(parameters);
    },
  });
};

export const generateTabReportPDF = async ({
  reportId,
  factPatternIds,
  renId,
  chkComment,
  keyValueInfo,
  templateIds,
  questionIds,
  downloadFile = 1,
}: GenerateTabReportPDFRequest): Promise<AxiosResponse<Blob>> => {
  const parameters = new URLSearchParams();
  
  parameters.append('downloadFile', String(downloadFile));
  parameters.append('reportId', String(reportId));
  parameters.append('renId', String(renId));
  parameters.append('chkComment', String(chkComment));
  parameters.append('keyValueInfo', keyValueInfo);
  parameters.append('templateIds', String(templateIds));
  parameters.append('questionIds', String(questionIds));
  
  for (const id of factPatternIds) {
    parameters.append('factPatternIds', String(id));
  }

  const response = await FileExportClient.get<Blob>(
    `/users/reports/tab/pdf/generate?${parameters.toString()}`,
    {
      responseType: 'blob',
    }
  );

  return response;
};

export const useGenerateTabReportPDFMutation = () => {
  return useMutation({
    mutationFn: async (parameters: GenerateTabReportPDFRequest) => {
      return generateTabReportPDF(parameters);
    },
  });
};

export interface GenerateReportExcelRequest {
  reportId: number;
  showBackInfo?: number;
  showComment?: number;
  templateIds?: string;
  questionIds?: string;
  factPatternIds?: string;
  renderingStyleId?: string | number;
  keyValue: string;
}

export const generateReportExcel = async ({
  reportId,
  showBackInfo = 1,
  showComment = 0,
  templateIds,
  questionIds,
  factPatternIds,
  keyValue,
}: GenerateReportExcelRequest): Promise<ExportReportExcelResponse> => {
  const parameters = new URLSearchParams();
  parameters.append('reportId', String(reportId));
  parameters.append('showBackInfo', String(showBackInfo));
  parameters.append('showComment', String(showComment));
  
  if (templateIds) {
    parameters.append('templateIds', templateIds);
  }
  if (questionIds) {
    parameters.append('questionIds', questionIds);
  }
  if (factPatternIds) {
    parameters.append('factPatternIds', factPatternIds);
  }
  parameters.append('keyValue', keyValue);

  const response = await FileExportClient.get<ExportReportExcelResponse>(
    `/users/reports/single-or-multi-template/excel/generate?${parameters.toString()}`,
  );

  return response.data;
};

export const useGenerateReportExcelMutation = () => {
  return useMutation({
    mutationFn: async (parameters: GenerateReportExcelRequest) => {
      return generateReportExcel(parameters);
    },
  });
};

export const generateTabReportExcel = async ({
  reportId,
  showBackInfo = 1,
  showComment = 0,
  keyValue,
  templateIds,
  questionIds,
  factPatternIds,
  renderingStyleId,
}: GenerateTabReportExcelRequest): Promise<ExportReportExcelResponse> => {
  const parameters = new URLSearchParams();
  parameters.append('reportId', String(reportId));
  parameters.append('showBackInfo', String(showBackInfo));
  parameters.append('showComment', String(showComment));
  parameters.append('keyValue', keyValue);
  parameters.append('templateIds', String(templateIds ?? '-1'));
  parameters.append('questionIds', String(questionIds ?? '-1'));
  parameters.append('factPatternIds', String(factPatternIds ?? '-1'));
  
  if (renderingStyleId) {
    parameters.append('renderingStyleId', String(renderingStyleId));
  }


  const response = await FileExportClient.get<ExportReportExcelResponse>(
    `/users/reports/tab/excel/generate?${parameters.toString()}`,
  );

  return response.data;
};

export const useGenerateTabReportExcelMutation = () => {
  return useMutation({
    mutationFn: async (parameters: GenerateTabReportExcelRequest) => {
      return generateTabReportExcel(parameters);
    },
  });
};

export interface GenerateReportCompareExcelRequest {
  reportIds: string;
  showBackInfo?: number;
  showComment?: number;
  questionIds?: string;
  templateIds?: string;
  factPatternIds?: string;
}

export const generateReportCompareExcel = async ({
  reportIds,
  showBackInfo = 0,
  showComment = 0,
  questionIds,
  templateIds,
  factPatternIds,
}: GenerateReportCompareExcelRequest): Promise<ExportReportExcelResponse> => {
  const parameters = new URLSearchParams();
  parameters.append('reportIds', reportIds);
  parameters.append('showBackInfo', String(showBackInfo));
  parameters.append('showComment', String(showComment));

  if (questionIds) {
    parameters.append('questionIds', questionIds);
  }
  if (templateIds) {
    parameters.append('templateIds', templateIds);
  }
  if (factPatternIds) {
    parameters.append('factPatternIds', factPatternIds);
  }

  const response = await FileExportClient.get<ExportReportExcelResponse>(
    `/users/report-compare/excel/generate?${parameters.toString()}`,
  );

  return response.data;
};

export const useGenerateReportCompareExcelMutation = () => {
  return useMutation({
    mutationFn: async (parameters: GenerateReportCompareExcelRequest) => {
      return generateReportCompareExcel(parameters);
    },
  });
};

export const useGetAllInternalContacts = (reportId: string) => {
  return useQuery({
    queryKey: [keyFactory.getAllInternalContacts, reportId],
    queryFn: async () => {
      const result = await HttpClient.get<ReportNoteContactsResponse>(
        `users/reports/add-notes-contacts-by-all-opinion-id?reportIds=${reportId}`
      );
      return result.data.data ?? null;
    },
    enabled: !!reportId,
  });
};


export const useGetAllInternalGuidances = (reportId: string) => {
  return useQuery({
    queryKey: [keyFactory.getAllInternalGuidances, reportId],
    queryFn: async () => {
      const result = await HttpClient.get<ReportNoteGuidanceResponse>(
        `users/reports/add-notes-guidance-by-all-opinion-id?reportIds=${reportId}`
      );
      return result.data.data ?? null;
    },
    enabled: !!reportId,
  });
};

export const useGetAllInternalResource = (reportId: string) => {
  return useQuery({
    queryKey: [keyFactory.getAllInternalResource, reportId],
    queryFn: async () => {
      const result = await HttpClient.get<ReportNoteGuidanceResponse>(
        `users/reports/add-notes-guidance-by-all-opinion-id?reportIds=${reportId}`
      );
      return result.data.data ?? null;
    },
    enabled: !!reportId,
  });
};

export const useGetAllInternalReference = (reportId: string) => {
  return useQuery({
    queryKey: [keyFactory.getAllInternalReference, reportId],
    queryFn: async () => {
      const result = await HttpClient.get<InternalReferenceResponse>(
        `users/reports/opinion-organization-notes?reportIds=${reportId}`
      );
      return result.data.data ?? null;
    },
    enabled: !!reportId,
  });
};

export interface OrganisationNotesResponse {
  opinionId: number;
  internalRef: string | null;
  notes: string | null;
}

export const useGetOrganisationNotes = (reportId: string) => {
  return useQuery({
    queryKey: ['organisationNotes', reportId],
    queryFn: async () => {
      const result = await HttpClient.get<GlobalResponse<Array<OrganisationNotesResponse>>>(
        `users/reports/${reportId}/organisation-notes`
      );
      return result.data.data ?? null;
    },
    enabled: !!reportId,
  });
};

export interface QuestionSearchParameters {
  orgIds: string;
  templateIds: string;
  questionIds: string;
  opinionIds: string;
  searchText: string;
  searchCondition?: 1 | 2 | 3; // 1 = Exact, 2 = All (AND), 3 = Any (OR)
}

export interface QuestionSearchResult {
  questionId: number;
  questionTitle: string;
  questionDescription: string | null;
  questionComment: string | null;
  displayOrder: number;
  templateId: number;
  subjectId: number;
  subjectName: string;
  isTreeTypeId: number;
  isTreeType: number;
  idRef: string | null;
  refQuelIDRef: string | null;
  edocId: string | null;
  epage: string | null;
  isDivider: number | null;
  count: number | null;
  questionCounter: number | null;
  rowId: number | null;
  selected: boolean;
  titleResponseType: number | null;
  xmlString: string | null;
  templateOpinionOrgId: number | null;
  opinionId: number | null;
  orgId: number | null;
  opinionTempQuesOrgId: number | null;
  templateName: string;
  templateDispOrder: number;
}

export interface QuestionSearchResponse {
  responseType: unknown;
  getQuesSubAnswerResponseType: unknown;
  getDividerQuestionResponseType: unknown;
  getTemplateCountByQuestionId: unknown;
  getDividerSubQuestionResponseType: unknown;
  getTemplateDetailByOpinionIdSearch: Array<unknown>;
  getAllTemplateQuestionDetail: Array<QuestionSearchResult>;
  getAllIsDividerQuestion?: Array<{
    questionId: number;
    templateId: number;
    isDivider: number;
    [key: string]: unknown;
  }>;
}

export const searchQuestions = async (
  parameters: QuestionSearchParameters
): Promise<QuestionSearchResponse> => {
  const queryParameters = new URLSearchParams({
    orgIds: parameters.orgIds,
    templateIds: parameters.templateIds,
    questionIds: parameters.questionIds,
    opinionIds: parameters.opinionIds,
    searchText: parameters.searchText,
    searchCondition: String(parameters.searchCondition ?? 2),
  });

  const result = await HttpClient.get<{ data: QuestionSearchResponse }>(
    `users/questions/search?${queryParameters.toString()}`
  );
  return result.data.data ?? null;
};

export const useGetReportNodesDetails = (
  reportId: number | null,
  nodeType: number = 1,
  options?: { enabled?: boolean }
) => {
  const enabled = options?.enabled ?? Boolean(reportId);

  return useQuery<ReportNodesDetailsResponse>({
    queryKey: [keyFactory.getReportNodesDetails, reportId, nodeType],
    queryFn: async () => {
      if (!reportId) throw new Error('Report ID is required.');
      const result = await HttpClient.get<ReportNodesDetailsResponse>(
        `/users/reports/nodes-details?reportId=${reportId}&nodeType=${nodeType}`
      );
      return result.data;
    },
    enabled,
  });
};

// get region name and id by jurisdiction id 
export const useGetRegionDataByJuriId = (jurisdictionId: string) => {
  const normalizedJurisdictionId = jurisdictionId?.trim?.() ?? '';
  const numericJurisdictionId = Number(normalizedJurisdictionId);
  const isValidJurisdictionId =
    normalizedJurisdictionId !== '' &&
    normalizedJurisdictionId !== 'null' &&
    Number.isFinite(numericJurisdictionId) &&
    numericJurisdictionId > 0;

  return useQuery({
    queryKey: [keyFactory.getRegionDataByJuriId, jurisdictionId],
    queryFn: async () => {
      const result = await HttpClient.get<GetRegionDataByJuriIdResponse>(`users/jurisdictions/continent-details?jurisdictionId=${jurisdictionId}`);
      return result.data.data ?? null;
    },
    enabled: isValidJurisdictionId
  });
};

export const useGetReportFileByApi = (reportId: string) => {
  return useQuery({
    queryKey: [keyFactory.fileByApiTypes, reportId],
    queryFn: async () => {
      const result = await HttpClient.get<GetReportFileByApiResponse>(
        `users/reports/file-by-api?reportId=${reportId}`
      );
      return result.data.data ?? null;
    },
    enabled: !!reportId,
  });
};